// Simplified Google Analytics 4 utility functions
// Following Google's 2025 best practices for React integration
// ENHANCED WITH DEBUG LOGGING FOR PRODUCTION TESTING

import { 
  trackNewsletterConversion, 
  trackMusicPlatformConversion, 
  trackLanguageChangeConversion, 
  trackEngagementConversion 
} from './conversions-api';
import { getFacebookParams } from './facebook-utils';

export const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || 'G-1YHGM6B737';

console.log('🔍 Analytics Module: Loaded with GA_TRACKING_ID =', GA_TRACKING_ID);

// Check if gtag is available (loaded via static HTML script)
export const isGAAvailable = (): boolean => {
  const available = typeof window !== 'undefined' && typeof window.gtag === 'function';
  console.log('🔍 Analytics: isGAAvailable() =', available);
  return available;
};

// Check if Facebook Pixel is loaded
export const isFacebookPixelLoaded = (): boolean => {
  const available = typeof window !== 'undefined' && typeof window.fbq === 'function';
  console.log('🔍 Analytics: isFacebookPixelLoaded() =', available);
  return available;
};

// Track custom events (GA4)
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  console.log('🔍 Analytics: trackEvent called with:', { action, category, label, value });
  
  if (!isGAAvailable()) {
    console.warn('⚠️ Analytics: GA4 not available - tracking skipped for:', action);
    return;
  }
  
  const eventData = {
    event_category: category,
    event_label: label,
    value: value,
  };
  
  console.log('📊 Analytics: Sending GA4 event:', action, 'with data:', eventData);
  
  window.gtag('event', action, eventData);
  
  console.log('✅ Analytics: GA4 event sent successfully:', action);
};

// Track music platform clicks - FIXED FOR META COMPLIANCE
export const trackMusicPlatformClick = async (platform: string, url: string) => {
  console.log('🎵 Analytics: trackMusicPlatformClick called');
  console.log('🔍 Analytics: Platform:', platform, '| URL:', url);

  // Extract Facebook parameters for EMQ improvement
  const facebookParams = getFacebookParams();
  console.log('🔍 Analytics: Facebook params extracted:', facebookParams);

  // Google Analytics tracking
  if (isGAAvailable()) {
    console.log('📊 Analytics: Sending to GA4...');
    
    trackEvent('click', 'Music Platform', platform);
    
    const ga4Data = {
      platform_name: platform,
      destination_url: url,
      content_type: 'music_link'
    };
    
    console.log('🔍 Analytics: GA4 music_platform_click data:', ga4Data);
    window.gtag('event', 'music_platform_click', ga4Data);
    console.log('✅ Analytics: GA4 music_platform_click sent successfully');
  } else {
    console.warn('⚠️ Analytics: GA4 not available, skipping GA4 tracking');
  }

  // Facebook Conversions API server-side tracking (SEND FIRST for event_id)
  console.log('🌐 Analytics: Sending to Conversions API...');
  let eventId: string | undefined;
  
  try {
    const result = await trackMusicPlatformConversion(platform, url, facebookParams);
    if (result.success && result.eventId) {
      eventId = result.eventId;
      console.log(`✅ Analytics: Conversions API event sent for ${platform}`);
      console.log('🔑 Analytics: Event ID for deduplication:', eventId);
    } else {
      console.warn('⚠️ Analytics: Conversions API succeeded but no event_id returned');
    }
  } catch (error) {
    console.error('❌ Analytics: Failed to track Conversions API event:', error);
  }

  // Facebook Pixel browser tracking - WITH DEDUPLICATION
  if (isFacebookPixelLoaded()) {
    console.log('📘 Analytics: Sending to Facebook Pixel (standard ViewContent event)...');
    
    const pixelData = {
      content_type: 'music_link',
      content_name: `${platform} Link`,
      content_category: 'Music Platform',
      value: 1,
      currency: 'USD'
    };
    
    const deduplicationOptions = eventId ? { eventID: eventId } : undefined;
    
    console.log('🔍 Analytics: Facebook ViewContent event data:', pixelData);
    console.log('🔑 Analytics: Deduplication options:', deduplicationOptions);
    
    if (deduplicationOptions) {
      window.fbq('track', 'ViewContent', pixelData, deduplicationOptions);
      console.log(`✅ Analytics: Facebook ViewContent event sent with deduplication for ${platform}`);
    } else {
    window.fbq('track', 'ViewContent', pixelData);
      console.warn(`⚠️ Analytics: Facebook ViewContent event sent WITHOUT deduplication for ${platform}`);
    }
  } else {
    console.warn('⚠️ Analytics: Facebook Pixel not available, skipping pixel tracking');
  }
  
  console.log('🏁 Analytics: trackMusicPlatformClick completed');
};

// Track newsletter subscription - CLEANED FOR META COMPLIANCE
export const trackNewsletterSubscription = async (email: string, countryData?: { country: string; country_code: string; detected_language?: string } | null) => {
  const emailDomain = email.split('@')[1];
  
  // Extract Facebook parameters for EMQ improvement
  const facebookParams = getFacebookParams();
  console.log('🔍 Analytics: Facebook params extracted:', facebookParams);
  
  console.log('📧 Analytics: trackNewsletterSubscription called');
  console.log('🔍 Analytics: Email domain:', emailDomain);
  console.log('🔍 Analytics: Country data:', countryData);
  console.log('🌍 Analytics: Multilingual data captured:', {
    detected_language: countryData?.detected_language,
    country: countryData?.country,
    country_code: countryData?.country_code
  });
  
  // Google Analytics tracking
  if (isGAAvailable()) {
    console.log('📊 Analytics: Sending newsletter data to GA4...');
    
    trackEvent('subscribe', 'Newsletter', emailDomain);
    
    const ga4Data = {
      email_domain: emailDomain,
      method: 'email_form',
      country: countryData?.country,
      country_code: countryData?.country_code,
      detected_language: countryData?.detected_language
    };
    
    console.log('🔍 Analytics: GA4 newsletter_signup data:', ga4Data);
    window.gtag('event', 'newsletter_signup', ga4Data);
    console.log('✅ Analytics: GA4 newsletter_signup sent successfully');
  } else {
    console.warn('⚠️ Analytics: GA4 not available, skipping GA4 newsletter tracking');
  }

  // Facebook Conversions API server-side tracking (SEND FIRST for event_id)
  console.log('🌐 Analytics: Sending newsletter data to Conversions API...');
  console.log('🔍 Analytics: Conversions API will receive country data:', countryData);
  console.log('🔍 Analytics: Conversions API will receive Facebook params:', facebookParams);
  let eventId: string | undefined;
  
  try {
    const result = await trackNewsletterConversion(email, countryData, facebookParams);
    if (result.success && result.eventId) {
      eventId = result.eventId;
      console.log('✅ Analytics: Conversions API newsletter subscription sent successfully');
      console.log('🔑 Analytics: Event ID for deduplication:', eventId);
    } else {
      console.warn('⚠️ Analytics: Conversions API succeeded but no event_id returned');
    }
  } catch (error) {
    console.error('❌ Analytics: Failed to track Conversions API newsletter subscription:', error);
  }

  // Facebook Pixel browser tracking - WITH DEDUPLICATION
  if (isFacebookPixelLoaded()) {
    console.log('📘 Analytics: Sending newsletter data to Facebook Pixel (standard Lead event)...');
    
    const leadData = {
      content_name: 'Newsletter Subscription',
      content_category: 'Email Marketing',
      value: 1,
      currency: 'USD'
    };
    
    // Deduplication parameter
    const deduplicationOptions = eventId ? { eventID: eventId } : undefined;
    
    console.log('🔍 Analytics: Facebook Lead event data:', leadData);
    console.log('🔑 Analytics: Deduplication options:', deduplicationOptions);
    
    // Pass eventID as third parameter for deduplication
    if (deduplicationOptions) {
      window.fbq('track', 'Lead', leadData, deduplicationOptions);
      console.log('✅ Analytics: Facebook Lead event sent with deduplication ID');
    } else {
    window.fbq('track', 'Lead', leadData);
      console.warn('⚠️ Analytics: Facebook Lead event sent WITHOUT deduplication (no event_id)');
    }
  } else {
    console.warn('⚠️ Analytics: Facebook Pixel not available, skipping pixel newsletter tracking');
  }
  
  console.log('🏁 Analytics: trackNewsletterSubscription completed');
};

// Track language changes - SIMPLIFIED FOR META COMPLIANCE
export const trackLanguageChange = async (fromLang: string, toLang: string, changeType: string = 'manual') => {
  console.log('🌍 Analytics: trackLanguageChange called');
  console.log('🔍 Analytics: Language change:', fromLang, '→', toLang, '(', changeType, ')');
  
  // Google Analytics tracking (keep for analytics)
  if (isGAAvailable()) {
    console.log('📊 Analytics: Sending language change to GA4...');
    
    trackEvent('language_change', 'Localization', `${fromLang}_to_${toLang}`);
    
    const ga4Data = {
      from_language: fromLang,
      to_language: toLang,
      change_type: changeType
    };
    
    console.log('🔍 Analytics: GA4 language_change data:', ga4Data);
    window.gtag('event', 'language_change', ga4Data);
    console.log('✅ Analytics: GA4 language_change sent successfully');
  } else {
    console.warn('⚠️ Analytics: GA4 not available, skipping GA4 language tracking');
  }

  // Facebook Pixel browser tracking - REMOVED CUSTOM EVENT FOR COMPLIANCE
  if (isFacebookPixelLoaded()) {
    console.log('📘 Analytics: Facebook language change tracking disabled for Meta compliance (was custom LanguageChange event)');
  } else {
    console.warn('⚠️ Analytics: Facebook Pixel not available, skipping pixel language tracking');
  }

  // Facebook Conversions API server-side tracking - minimal for compliance
  console.log('🌐 Analytics: Language change Conversions API tracking minimal for compliance...');
  try {
    await trackLanguageChangeConversion(fromLang, toLang);
    console.log('✅ Analytics: Conversions API language change sent successfully');
  } catch (error) {
    console.error('❌ Analytics: Failed to track Conversions API language change:', error);
  }
  
  console.log('🏁 Analytics: trackLanguageChange completed');
};

// Track video view (ViewContent event)
export const trackVideoView = async (videoId: string, videoTitle: string) => {
  console.log('🎬 Analytics: trackVideoView called');
  console.log('🔍 Analytics: Video ID:', videoId, 'Title:', videoTitle);

  // Extract Facebook parameters for EMQ
  const facebookParams = getFacebookParams();
  console.log('🔍 Analytics: Facebook params extracted:', facebookParams);

  // Google Analytics tracking
  if (isGAAvailable()) {
    console.log('📊 Analytics: Sending video view to GA4...');
    
    trackEvent('view_item', 'Video', videoTitle);
    
    const ga4Data = {
      content_type: 'video',
      content_id: videoId,
      content_name: videoTitle,
      video_provider: 'youtube'
    };
    
    console.log('🔍 Analytics: GA4 view_item data:', ga4Data);
    window.gtag('event', 'view_item', ga4Data);
    console.log('✅ Analytics: GA4 view_item sent successfully');
  } else {
    console.warn('⚠️ Analytics: GA4 not available, skipping GA4 video tracking');
  }

  // Facebook Conversions API server-side tracking (FIRST - for event_id generation)
  console.log('🌐 Analytics: Sending video view to Conversions API...');
  let eventId: string | undefined;
  
  try {
    // Note: We don't have a video conversion function yet, so we'll use ViewContent wrapper
    // You can extend conversions-api.ts to add a trackVideoConversion function
    const result = await trackEngagementConversion(
      'video_view',
      videoTitle,
      videoId,
      facebookParams
    );
    
    if (result.success && result.eventId) {
      eventId = result.eventId;
      console.log('✅ Analytics: Conversions API video view sent successfully');
      console.log('🔑 Analytics: Event ID for deduplication:', eventId);
    } else {
      console.warn('⚠️ Analytics: Conversions API succeeded but no event_id returned');
    }
  } catch (error) {
    console.error('❌ Analytics: Failed to track Conversions API video view:', error);
  }

  // Facebook Pixel browser tracking (SECOND - with deduplication)
  if (isFacebookPixelLoaded()) {
    console.log('📘 Analytics: Sending video view to Facebook Pixel...');
    
    const pixelData = {
      content_name: videoTitle,
      content_category: 'Video',
      content_type: 'video',
      content_ids: [videoId],
      value: 1,
      currency: 'USD'
    };
    
    const deduplicationOptions = eventId ? { eventID: eventId } : undefined;
    console.log('🔍 Analytics: Facebook ViewContent event data:', pixelData);
    console.log('🔑 Analytics: Deduplication options:', deduplicationOptions);
    
    if (deduplicationOptions) {
      window.fbq('track', 'ViewContent', pixelData, deduplicationOptions);
      console.log('✅ Analytics: Facebook ViewContent event sent with deduplication ID');
    } else {
      window.fbq('track', 'ViewContent', pixelData);
      console.warn('⚠️ Analytics: Facebook ViewContent event sent WITHOUT deduplication (no event_id)');
    }
  } else {
    console.warn('⚠️ Analytics: Facebook Pixel not available, skipping pixel video tracking');
  }

  console.log('🏁 Analytics: trackVideoView completed');
};

// Track user engagement - SIMPLIFIED FOR META COMPLIANCE
export const trackEngagement = async (action: string, element: string) => {
  console.log('🎯 Analytics: trackEngagement called');
  console.log('🔍 Analytics: Action:', action, '| Element:', element);
  
  // Google Analytics tracking (keep for analytics)
  if (isGAAvailable()) {
    console.log('📊 Analytics: Sending engagement to GA4...');
    trackEvent(action, 'Engagement', element);
    console.log('✅ Analytics: GA4 engagement event sent successfully');
  } else {
    console.warn('⚠️ Analytics: GA4 not available, skipping GA4 engagement tracking');
  }

  // Facebook Pixel browser tracking - REMOVED CUSTOM EVENT FOR COMPLIANCE
  if (isFacebookPixelLoaded()) {
    console.log('📘 Analytics: Facebook engagement tracking disabled for Meta compliance (was custom UserEngagement event)');
  } else {
    console.warn('⚠️ Analytics: Facebook Pixel not available, skipping pixel engagement tracking');
  }

  // Facebook Conversions API server-side tracking - minimal for compliance
  console.log('🌐 Analytics: Engagement Conversions API tracking minimal for compliance...');
  try {
    await trackEngagementConversion(action, element);
    console.log('✅ Analytics: Conversions API engagement sent successfully');
  } catch (error) {
    console.error('❌ Analytics: Failed to track Conversions API engagement:', error);
  }
  
  console.log('🏁 Analytics: trackEngagement completed');
};

// Enhanced debug function for production testing
export const debugGA = () => {
  console.log('=== 🔍 COMPREHENSIVE ANALYTICS DEBUG INFO ===');
  console.log('📊 Google Analytics 4:');
  console.log('  • GA_TRACKING_ID:', GA_TRACKING_ID);
  console.log('  • isGAAvailable():', isGAAvailable());
  console.log('  • window.gtag type:', typeof window.gtag);
  console.log('  • window.dataLayer exists:', !!window.dataLayer);
  console.log('  • window.dataLayer length:', window.dataLayer?.length || 0);
  
  console.log('📘 Facebook Pixel:');
  console.log('  • isFacebookPixelLoaded():', isFacebookPixelLoaded());
  console.log('  • window.fbq type:', typeof window.fbq);
  
  console.log('🌍 Environment:');
  console.log('  • Current URL:', window.location.href);
  console.log('  • User Agent:', navigator.userAgent.substring(0, 50) + '...');
  console.log('  • Language:', navigator.language);
  console.log('  • Languages:', navigator.languages);
  
  if (isGAAvailable()) {
    console.log('✅ GA4 Test: Sending debug test event...');
    window.gtag('event', 'debug_test', {
      event_category: 'debug',
      event_label: 'production_test',
      value: 1
    });
    console.log('✅ GA4 Test: Debug event sent successfully');
  } else {
    console.error('❌ GA4 Test: Cannot send test event - GA4 not available');
  }
  
  if (isFacebookPixelLoaded()) {
    console.log('✅ Facebook Test: Sending debug test event...');
    window.fbq('trackCustom', 'DebugTest', {
      source: 'production_debug',
      timestamp: new Date().toISOString()
    });
    console.log('✅ Facebook Test: Debug event sent successfully');
  } else {
    console.error('❌ Facebook Test: Cannot send test event - Facebook Pixel not available');
  }
  
  console.log('=== END COMPREHENSIVE DEBUG INFO ===');
  
  // Return summary object for programmatic access
  return {
    ga4: {
      available: isGAAvailable(),
      trackingId: GA_TRACKING_ID,
      dataLayerLength: window.dataLayer?.length || 0
    },
    facebook: {
      available: isFacebookPixelLoaded()
    },
    environment: {
      url: window.location.href,
      language: navigator.language,
      userAgent: navigator.userAgent
    }
  };
};

// Make debug function globally available
if (typeof window !== 'undefined') {
  window.debugGA = debugGA;
  
  // Auto-debug on page load (can be removed after testing)
  window.addEventListener('load', () => {
    setTimeout(() => {
      console.log('🚀 Auto-Debug: Running analytics debug on page load...');
      debugGA();
    }, 1000);
  });
}

// Extend window interface for TypeScript
declare global {
  interface Window {
    debugGA: () => any;
  }
}